import { UseFormReturn, useWatch } from 'react-hook-form';
import { IDocumentField, ISetValue } from '@models/Forms/IForms';
import { handlerFieldReplaceWatch, handlerFieldWatch } from '@utils/fieldWatchUtils';
import FieldWrapper from '../FieldWrapper/FieldWrapper';
import CheckboxBitControl from '@controls/CheckboxBitControl';
import YesNoControl from '@controls/YesNoControl';
import TextareaControl from '@controls/TextareaControl';
import TextareaBase64Control from '@controls/TextareaBase64Control';
import InputNumberControl from '@controls/InputNumberControl';
import DateTimepickerControl from '@controls/DateTimepickerControl';
import YearPickerControl from '@controls/YearPickerControl';
import DatepickerControl from '@controls/DatepickerControl';
import InputControl from '@controls/InputControl';
import './Field.scss';
import { IFieldElem } from '@models/IFormData';
import { FormulaManager } from '@utils/managers/FormulaManager';
import React, { useEffect, useMemo, useState } from 'react';
import DisplayField from '@atoms/DisplayField/DisplayField';
import { ChangesFieldValue, GetValueForSetValue } from '@utils/managers/ChangesManager';
import { ValueType } from '@/types/ValueType';
import ToolTipTitle from '@atoms/ToolTipTitle/ToolTipTitle';
import Moment from 'moment';
import { IPartialFormGroup } from '@controls/types';
import { declensionOfSymbol } from '@utils/helpers';
import { useWorkCalendar } from '@hooks/useWorkCalendar';

export interface IFieldProps<TFieldValues extends object = object> {
    field?: IDocumentField;
    formMethods: UseFormReturn<TFieldValues>;
    setError: (errors?: string[]) => void;
    isEdit: boolean;
    isNew: boolean;
    turnOffInvokeChangesOnNew: boolean;
    fields: Record<string, IFieldElem>;
    includedFields: number[];
}

const Field = <TFieldValues extends object = object>({
    field,
    formMethods,
    setError,
    isEdit,
    isNew,
    turnOffInvokeChangesOnNew,
    fields,
    includedFields,
    ...props
}: IFieldProps<TFieldValues>) => {
    const visibilityRules = field?.visibilityRules;
    const readOnlyRules = field?.readonly;
    const requiredRules = field?.required;
    const minRules = field?.min;
    const maxRules = field?.max;
    const workCalendarRules = field?.workCalendarRules;

    const visibilityMng = useMemo(() => {
        const manager = new FormulaManager(visibilityRules!);
        manager.Init(fields, formMethods);
        return manager;
    }, [visibilityRules, fields, formMethods]);

    const readOnlyMng = useMemo(() => {
        const manager = new FormulaManager(readOnlyRules!);
        manager.Init(fields, formMethods);
        return manager;
    }, [readOnlyRules, fields, formMethods]);

    const requiredMng = useMemo(() => {
        const manager = new FormulaManager(requiredRules!);
        manager.Init(fields, formMethods);
        return manager;
    }, [requiredRules, fields, formMethods]);

    const minMng = useMemo(() => {
        const manager = new FormulaManager(minRules!);
        manager.Init(fields, formMethods);
        return manager;
    }, [minRules, fields, formMethods]);

    const maxMng = useMemo(() => {
        const manager = new FormulaManager(maxRules!);
        manager.Init(fields, formMethods);
        return manager;
    }, [maxRules, fields, formMethods]);

    const workCalendarMng = useMemo(() => {
        const manager = new FormulaManager(workCalendarRules!);
        manager.Init(fields, formMethods);
        return manager;
    }, [workCalendarRules, fields, formMethods]);

    const activated = React.useRef(false);
    const [visibility, setVisibility] = useState<boolean>(false);
    const [required, setRequired] = useState<boolean>(false);
    const [readOnly, setReadOnly] = useState<boolean>(false);
    const [minVal, setMinVal] = useState<any>();
    const [maxVal, setMaxVal] = useState<any>();
    const [workCalendar, setWorkCalendar] = useState<boolean>(false);
    const calendar = useWorkCalendar(workCalendar);

    const dataField = fields[field?.key!];
    const idField = dataField?.name;
    includedFields.push(dataField?.index);
    const fieldVal = dataField?.value;

    const InitFormulas = async () => {
        const vis = await visibilityMng.EvalFormulaValues(isEdit, isNew);
        if (activated.current) {
            setVisibility(vis);
        }

        if (requiredRules) {
            const req = await requiredMng.EvalFormulaValues(isEdit, isNew);
            if (activated.current) {
                setRequired(req);
            }
        }

        if (readOnlyRules) {
            const readonly = await readOnlyMng.EvalFormulaValues(isEdit, isNew);
            if (activated.current) {
                setReadOnly(readonly);
            }
        }

        if (minRules) {
            const d = await minMng.ReplaceFormulaValues(true, true, undefined, false);
            if (d) {
                setMinVal(d);
            }
        }

        if (maxRules) {
            const d = await maxMng.ReplaceFormulaValues(true, true, undefined, false);
            if (d) {
                setMaxVal(d);
            }
        }

        if (workCalendarRules) {
            const isWorkCalendar = await readOnlyMng.EvalFormulaValues(isEdit, isNew);
            if (activated.current) {
                setWorkCalendar(isWorkCalendar);
            }
        }

        if (field?.valueType == ValueType.Boolean && (fieldVal == undefined || fieldVal == null) && (isNew || isEdit)) {
            formMethods.setValue(idField as any, 0 as any, { shouldDirty: true });
        }
        if (isNew && field?.invokeChangesOnNew && vis && !turnOffInvokeChangesOnNew) {
            await ChangesFieldValue(
                field?.changes!,
                fieldVal,
                field?.key!,
                fields,
                isEdit,
                isNew,
                onSaveField,
                setError,
                undefined,
                undefined,
                formMethods,
            );
        }
    };

    useEffect(() => {
        if (isEdit && field?.invokeChangesOnEdit && visibility) {
            if (field?.valueType == ValueType.Boolean && fieldVal == undefined && (isEdit || isNew)) {
                formMethods.setValue(idField as any, 0 as any, { shouldDirty: true });
            }
            ChangesFieldValue(
                field?.changes!,
                fieldVal,
                field?.key!,
                fields,
                isEdit,
                isNew,
                onSaveField,
                setError,
                undefined,
                undefined,
                formMethods,
            );
        }
    }, [isEdit]);

    useEffect(() => {
        activated.current = true;
        InitFormulas();
        return () => {
            activated.current = false;
        };
    }, []);

    const watchVisibility = useWatch({
        name: visibilityMng.GetWatchFields(),
    });
    const watchReadOnly = useWatch({
        name: readOnlyMng.GetWatchFields(),
    });
    const watchRequired = useWatch({
        name: requiredMng.GetWatchFields(),
    });
    const watchMinDate = useWatch({
        name: minMng.GetWatchFields(),
    });
    const watchMaxDate = useWatch({
        name: maxMng.GetWatchFields(),
    });
    const watchWorkCalendar = useWatch({
        name: workCalendarMng.GetWatchFields(),
    });
    useEffect(() => {
        handlerFieldWatch(watchVisibility, isEdit, isNew, visibilityMng, setVisibility, activated);
    }, [watchVisibility, isEdit, isNew]);
    useEffect(() => {
        if (readOnlyRules) {
            handlerFieldWatch(watchReadOnly, isEdit, isNew, readOnlyMng, setReadOnly, activated);
        }
    }, [watchReadOnly, isEdit, isNew]);
    useEffect(() => {
        if (requiredRules) {
            handlerFieldWatch(watchRequired, isEdit, isNew, requiredMng, setRequired, activated);
        }
    }, [watchRequired, isEdit, isNew]);
    useEffect(() => {
        if (minRules) {
            handlerFieldReplaceWatch(watchMinDate, isEdit, isNew, minMng, setMinVal);
        }
    }, [watchMinDate, isEdit, isNew]);
    useEffect(() => {
        if (maxRules) {
            handlerFieldReplaceWatch(watchMaxDate, isEdit, isNew, maxMng, setMaxVal);
        }
    }, [watchMaxDate, isEdit, isNew]);
    useEffect(() => {
        if (workCalendarRules) {
            handlerFieldWatch(watchWorkCalendar, isEdit, isNew, workCalendarMng, setWorkCalendar, activated);
        }
    }, [watchWorkCalendar, isEdit, isNew]);

    const getValue = (value: any) => {
        let fieldValue = value;

        if (field?.replacerFrom && field?.replacerTo && !(fieldValue === undefined || fieldValue === null)) {
            const strValue = fieldValue as string;
            if (strValue) {
                fieldValue = strValue.replaceAll(field?.replacerFrom!, field?.replacerTo!);
            }
        }

        if (field?.valueType == ValueType.Url && field?.pattern) {
            if (fieldValue === undefined || fieldValue === null) {
                return '';
            }
            let val = field?.pattern;
            val = val.replace('{value}', fieldValue as string);
            return val;
        } else {
            return fieldValue;
        }
    };

    const onSaveField = async (item: ISetValue, rowData?: any) => {
        let val = await GetValueForSetValue(item, undefined, fields, rowData, formMethods);
        let field = fields[item.key];
        if (fields && field) {
            formMethods.setValue(field.name as any, val, { shouldDirty: true });
        }
    };
    const onInputChange = async (value: string) => {
        await ChangesFieldValue(
            field?.changes!,
            value,
            field?.key!,
            fields,
            isEdit,
            isNew,
            onSaveField,
            setError,
            undefined,
            undefined,
            formMethods,
        );
    };
    const onInputNumberChange = async (value: number) => {
        await ChangesFieldValue(
            field?.changes!,
            value,
            field?.key!,
            fields,
            isEdit,
            isNew,
            onSaveField,
            setError,
            undefined,
            undefined,
            formMethods,
        );
    };
    const onCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        let val = event.currentTarget.checked ? 1 : 0;
        await ChangesFieldValue(
            field?.changes!,
            val,
            field?.key!,
            fields,
            isEdit,
            isNew,
            onSaveField,
            setError,
            undefined,
            undefined,
            formMethods,
        );
    };
    const onTextareaChange = async (value: string) => {
        await ChangesFieldValue(
            field?.changes!,
            value,
            field?.key!,
            fields,
            isEdit,
            isNew,
            onSaveField,
            setError,
            undefined,
            undefined,
            formMethods,
        );
    };

    const onDateTimeChange = async (value: Date) => {
        await ChangesFieldValue(
            field?.changes!,
            value,
            field?.key!,
            fields,
            isEdit,
            isNew,
            onSaveField,
            setError,
            undefined,
            undefined,
            formMethods,
        );
    };
    const checkDate = (value: any) => {
        try {
            if (required && (value === undefined || value === null || value === '')) {
                return 'Обязательное поле';
            }
            let val = new Date(value);
            val.setSeconds(0);
            val.setMilliseconds(0);
            if (field?.valueType == ValueType.Date) {
                val.setHours(0);
                val.setMinutes(0);
            }

            if (field?.max && value !== undefined && value !== null && value !== '') {
                let valMax = new Date(+maxVal);
                valMax.setSeconds(0);
                valMax.setMilliseconds(0);
                if (field?.valueType == ValueType.Date) {
                    valMax.setHours(0);
                    valMax.setMinutes(0);
                }

                if (val.getTime() > valMax.getTime()) {
                    if (field?.valueType == ValueType.Date) {
                        return 'Максимальное значение: ' + Moment(valMax).format('DD.MM.YYYY');
                    } else {
                        return 'Максимальное значение: ' + Moment(valMax).format('DD.MM.YYYY HH:mm');
                    }
                }
            }
            if (field?.min && value !== undefined && value !== null && value !== '') {
                let valMin = new Date(+minVal);
                valMin.setSeconds(0);
                valMin.setMilliseconds(0);
                if (field?.valueType == ValueType.Date) {
                    valMin.setHours(0);
                    valMin.setMinutes(0);
                }
                if (val.getTime() < valMin.getTime()) {
                    if (field?.valueType == ValueType.Date) {
                        return 'Минимальное значение: ' + Moment(valMin).format('DD.MM.YYYY');
                    } else {
                        return 'Минимальное значение: ' + Moment(valMin).format('DD.MM.YYYY HH:mm');
                    }
                }
            }

            if (workCalendar && calendar.isHoliday(val)) {
                return 'Выбран выходной день';
            }

            return true;
        } catch (error) {
            console.log(error);
            return true;
        }
    };

    const fieldControlType = () => {
        if (field && (field.name === undefined || field.name === null)) {
            return <div />;
        }

        let rules = {
            ...{
                required: {
                    value: required ? required : false,
                    message: 'Обязательное поле',
                },
            },
            ...(field?.pattern &&
                field?.valueType != ValueType.Url && {
                    pattern: {
                        value: new RegExp(field?.pattern, 'g'),
                        message: 'Неверный формат',
                    },
                }),

            ...(field?.min &&
                (field?.valueType == ValueType.Text ||
                    field?.valueType == ValueType.LongText ||
                    field?.valueType == ValueType.Url) && {
                    minLength: {
                        value: Number(field?.min),
                        message: `Минимальная длина поля: ${field?.min} ${declensionOfSymbol(Number(field?.min))}`,
                    },
                }),
            ...(field?.max &&
                (field?.valueType == ValueType.Text ||
                    field?.valueType == ValueType.LongText ||
                    field?.valueType == ValueType.Url) && {
                    maxLength: {
                        value: Number(field?.max),
                        message: `Максимальная длина поля: ${field?.max} ${declensionOfSymbol(Number(field?.max))}`,
                    },
                }),
            ...(field?.max == undefined &&
                field?.valueType == ValueType.Text && {
                    maxLength: {
                        value: Number(255),
                        message: 'Максимальная длина поля: 255 символов',
                    },
                }),
        };
        if (
            rules.required == undefined &&
            formMethods.formState.errors &&
            (formMethods.formState.errors as any).fields &&
            (formMethods.formState.errors as any).fields[dataField?.index]
        ) {
            formMethods.clearErrors(idField as any);
        }
        switch (field?.valueType) {
            case ValueType.Boolean:
                return (
                    <CheckboxBitControl
                        label={field?.name}
                        tooltip={<ToolTipTitle title={field?.title} />}
                        readOnly={readOnly}
                        disabled={readOnly}
                        required={required}
                        formGroupProps={{ verticalBottomAlign: field?.verticalBottomAlign } as IPartialFormGroup}
                        rules={rules}
                        name={idField}
                        formState={formMethods.formState}
                        control={formMethods.control}
                        onChange={onCheckboxChange}
                        testid={field?.key}
                    />
                );
            case ValueType.YesNo:
                return (
                    <YesNoControl
                        label={field?.name}
                        trueText={field?.trueText}
                        falseText={field?.falseText}
                        tooltip={<ToolTipTitle title={field?.title} />}
                        disabled={readOnly}
                        required={required}
                        formGroupProps={{ verticalBottomAlign: field?.verticalBottomAlign } as IPartialFormGroup}
                        options={[]}
                        rules={rules}
                        name={idField}
                        formState={formMethods.formState}
                        control={formMethods.control}
                        onChangeVal={onInputNumberChange}
                        placeholder={field?.placeholder ? field.placeholder : undefined}
                        testid={field?.key}
                    />
                );
            case ValueType.LongText:
                return (
                    <TextareaControl
                        label={field?.name}
                        tooltip={<ToolTipTitle title={field?.title} />}
                        readOnly={readOnly}
                        required={required}
                        formGroupProps={{ verticalBottomAlign: field?.verticalBottomAlign } as IPartialFormGroup}
                        autoResize={field?.autoResize}
                        rules={rules}
                        name={idField}
                        formState={formMethods.formState}
                        control={formMethods.control}
                        onValueChange={onTextareaChange}
                        maxLength={field?.maxLength}
                        placeholder={field?.placeholder ? field.placeholder : undefined}
                        aiAutocompleteKey={field?.aiAutocompleteKey}
                        testid={field?.key}
                    />
                );
            case ValueType.Base64Text:
                return (
                    <TextareaBase64Control
                        label={field?.name}
                        tooltip={<ToolTipTitle title={field?.title} />}
                        readOnly={readOnly}
                        required={required}
                        formGroupProps={{ verticalBottomAlign: field?.verticalBottomAlign } as IPartialFormGroup}
                        autoResize={field?.autoResize}
                        rules={rules}
                        name={idField}
                        formState={formMethods.formState}
                        control={formMethods.control}
                        onValueChange={onTextareaChange}
                        placeholder={field?.placeholder ? field.placeholder : undefined}
                        testid={field?.key}
                    />
                );
            case ValueType.Double:
                return (
                    <InputNumberControl
                        label={field?.name}
                        tooltip={<ToolTipTitle title={field?.title} />}
                        formGroupProps={{ verticalBottomAlign: field?.verticalBottomAlign } as IPartialFormGroup}
                        groupBy={0}
                        separator={''}
                        readOnly={readOnly}
                        required={required}
                        max={field?.max ? Number(maxVal!) : undefined}
                        min={field?.min ? Number(minVal!) : undefined}
                        floatPoints={field?.floatPoints ?? 2}
                        mask={field?.mask}
                        rules={rules}
                        name={idField}
                        formState={formMethods.formState}
                        control={formMethods.control}
                        onValueChange={onInputNumberChange}
                        placeholder={field?.placeholder ? field.placeholder : undefined}
                        testid={field?.key}
                    />
                );
            case ValueType.Money:
                return (
                    <InputNumberControl
                        label={field?.name}
                        tooltip={<ToolTipTitle title={field?.title} />}
                        formGroupProps={{ verticalBottomAlign: field?.verticalBottomAlign } as IPartialFormGroup}
                        readOnly={readOnly}
                        required={required}
                        max={field?.max ? Number(maxVal!) : undefined}
                        min={field?.min ? Number(minVal!) : undefined}
                        floatPoints={field?.floatPoints ?? 2}
                        rules={rules}
                        name={idField}
                        mask={field?.mask}
                        formState={formMethods.formState}
                        control={formMethods.control}
                        onValueChange={onInputNumberChange}
                        placeholder={field?.placeholder ? field.placeholder : undefined}
                        testid={field?.key}
                    />
                );
            case ValueType.Integer:
                return (
                    <InputNumberControl
                        label={field?.name}
                        tooltip={<ToolTipTitle title={field?.title} />}
                        formGroupProps={{ verticalBottomAlign: field?.verticalBottomAlign } as IPartialFormGroup}
                        groupBy={0}
                        separator={''}
                        readOnly={readOnly}
                        required={required}
                        max={field?.max ? Number(maxVal!) : undefined}
                        min={field?.min ? Number(minVal!) : undefined}
                        rules={rules}
                        name={idField}
                        floatPoints={0}
                        //type="number"
                        pattern={'^[\\d]+$'}
                        mask={field?.mask}
                        formState={formMethods.formState}
                        control={formMethods.control}
                        onValueChange={onInputNumberChange}
                        placeholder={field?.placeholder ? field.placeholder : undefined}
                        testid={field?.key}
                    />
                );
            case ValueType.NoSecDateTime:
                return (
                    <DateTimepickerControl
                        label={field?.name}
                        tooltip={<ToolTipTitle title={field?.title} />}
                        formGroupProps={{ verticalBottomAlign: field?.verticalBottomAlign } as IPartialFormGroup}
                        readOnly={readOnly}
                        required={required}
                        defaultValue={fieldVal as any}
                        rules={{
                            validate: { checkDate },
                        }}
                        name={idField}
                        maxDate={field?.max && maxVal && maxVal !== 'null' ? new Date(+maxVal!) : undefined}
                        minDate={field?.min && minVal && minVal !== 'null' ? new Date(+minVal!) : undefined}
                        workCalendar={workCalendar}
                        formState={formMethods.formState}
                        control={formMethods.control}
                        isNoSecFormat={true}
                        onChangeVal={onDateTimeChange}
                        placeholder={field?.placeholder ? field.placeholder : undefined}
                        testid={field?.key}
                    />
                );
            case ValueType.Year:
                return (
                    <YearPickerControl
                        label={field?.name}
                        tooltip={<ToolTipTitle title={field?.title} />}
                        formGroupProps={{ verticalBottomAlign: field?.verticalBottomAlign } as IPartialFormGroup}
                        disabled={readOnly}
                        required={required}
                        max={field?.max ? Number(maxVal!) : 2050}
                        min={field?.min ? Number(minVal!) : 1950}
                        defaultValue={fieldVal as any}
                        rules={rules}
                        name={idField}
                        formState={formMethods.formState}
                        control={formMethods.control}
                        onChangeVal={onInputNumberChange}
                        options={[]}
                        placeholder={field?.placeholder ? field.placeholder : undefined}
                        testid={field?.key}
                    />
                );
            case ValueType.DateTime:
                return (
                    <DateTimepickerControl
                        label={field?.name}
                        tooltip={<ToolTipTitle title={field?.title} />}
                        formGroupProps={{ verticalBottomAlign: field?.verticalBottomAlign } as IPartialFormGroup}
                        readOnly={readOnly}
                        required={required}
                        defaultValue={fieldVal as any}
                        rules={{
                            validate: { checkDate },
                        }}
                        name={idField}
                        maxDate={field?.max && maxVal && maxVal !== 'null' ? new Date(+maxVal!) : undefined}
                        minDate={field?.min && minVal && minVal !== 'null' ? new Date(+minVal!) : undefined}
                        formState={formMethods.formState}
                        control={formMethods.control}
                        isNoSecFormat={false}
                        onChangeVal={onDateTimeChange}
                        placeholder={field?.placeholder ? field.placeholder : undefined}
                        testid={field?.key}
                    />
                );
            case ValueType.Date:
                return (
                    <DatepickerControl
                        label={field?.name}
                        tooltip={<ToolTipTitle title={field?.title} />}
                        formGroupProps={{ verticalBottomAlign: field?.verticalBottomAlign } as IPartialFormGroup}
                        readOnly={readOnly}
                        required={required}
                        defaultValue={fieldVal as any}
                        rules={{
                            validate: { checkDate },
                        }}
                        name={idField}
                        maxDate={field?.max && maxVal && maxVal !== 'null' ? new Date(+maxVal!) : undefined}
                        minDate={field?.min && minVal && minVal !== 'null' ? new Date(+minVal!) : undefined}
                        workCalendar={workCalendar}
                        formState={formMethods.formState}
                        control={formMethods.control}
                        onChangeVal={onDateTimeChange}
                        isValueAsString={field?.isValueAsString}
                        placeholder={field?.placeholder ? field.placeholder : undefined}
                        testid={field?.key}
                    />
                );
            case ValueType.Url:
            case ValueType.Text:
                return (
                    <InputControl
                        label={field?.name}
                        tooltip={<ToolTipTitle title={field?.title} />}
                        formGroupProps={{ verticalBottomAlign: field?.verticalBottomAlign } as IPartialFormGroup}
                        readOnly={readOnly}
                        required={required}
                        rules={rules}
                        name={idField}
                        mask={field?.mask}
                        formState={formMethods.formState}
                        control={formMethods.control}
                        onValueChange={onInputChange}
                        pattern={field?.inputRegExp}
                        placeholder={field?.placeholder ? field.placeholder : undefined}
                        testid={field?.key}
                    />
                );

            default:
                return (
                    <div>
                        {field?.valueType} --- {fieldVal && fieldVal.toString()} --- {typeof fieldVal}
                    </div>
                );
        }
    };

    return field && field.key && visibility && idField != undefined ? (
        <div className="form-field" data-testid={field.id ? `field-${field.id}` : undefined}>
            {isEdit || isNew ? (
                fieldControlType()
            ) : (
                <FieldWrapper
                    inLineFormat={field.isValue}
                    label={field.valueType === ValueType.Boolean ? undefined : field.name}
                    title={field.viewTitle}
                    verticalBottomAlign={field?.verticalBottomAlign}
                >
                    <DisplayField
                        type={field.valueType}
                        floatPoints={field.floatPoints}
                        label={field.name}
                        value={getValue(fieldVal)}
                        trueText={field.trueText}
                        falseText={field.falseText}
                        testid={field.key}
                    />
                </FieldWrapper>
            )}
        </div>
    ) : (
        <></>
    );
};

export default Field;
